import { unwrap } from "@sagot/api-client";
import { AUDIT_PREFIXES, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, ErrorState, Icon, Skeleton, Tooltip, buttonClasses } from "@sagot/ui";
import { useQueries } from "@tanstack/react-query";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, type AuditLogOut } from "../../shared";
import { humanizeAction } from "../settings-model";
import { actionKey, useAuditCsv } from "./audit-trails";

const K = "privacyM29.audit";
const ROWS = 5;

/** The newest rows of each trail (launches, customers, API keys), merged newest first. */
function useRecentSecurityActivity(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  const results = useQueries({
    queries: AUDIT_PREFIXES.map((prefix) => ({
      queryKey: ["ws", wsId, "settings", "audit", "security-activity", prefix] as const,
      queryFn: () => unwrap(api.GET("/v1/audit-logs", { params: { query: { action_prefix: prefix, limit: ROWS } } })),
      enabled,
    })),
  });
  const rows: AuditLogOut[] = results
    .flatMap((result) => result.data?.items ?? [])
    .sort((a, b) => b.occurred_at.localeCompare(a.occurred_at))
    .slice(0, ROWS);
  return {
    rows,
    isPending: enabled && results.some((result) => result.isPending),
    isError: results.some((result) => result.isError),
    refetch: () => Promise.all(results.map((result) => result.refetch())),
  };
}

function EventRow({ row }: { row: AuditLogOut }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const key = actionKey(row.action);
  const label = t(`${K}.events.${key}`, { defaultValue: t(`settings.audit.actions.${key}`, { defaultValue: humanizeAction(row.action) }) });
  return (
    <tr data-testid="security-activity-row">
      <td className="whitespace-nowrap font-mono text-xs text-fg-muted">{format.dateTime(row.occurred_at)}</td>
      <td>
        {label}
        {row.target_label ? <span className="text-fg-muted"> · {row.target_label}</span> : null}
      </td>
    </tr>
  );
}

/** Settings > Privacy > "Security activity" (privacy-security pin 5, SEC-006): the latest launches, lookups and views, a CSV export, and the way into the full audit log. */
export function SecurityActivityCard() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const canView = permissions.can("audit.view");
  const canExport = canView && permissions.can("data.export");
  const activity = useRecentSecurityActivity(canView);
  const csv = useAuditCsv();
  const exportButton = (
    <Button variant="secondary" size="sm" disabled={!canExport} loading={csv.isPending} onClick={() => { csv.mutate({}); }} data-testid="security-activity-export">
      <Icon name="download" />
      {t(`${K}.exportCsv`)}
    </Button>
  );
  return (
    <section className="card overflow-hidden" aria-labelledby="security-activity-title" data-testid="security-activity-card">
      <div className="card-header flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="security-activity-title" className="card-title">
            {t(`${K}.title`)}
          </h2>
          <p className="mt-1 text-xs text-fg-muted">{t(`${K}.intro`)}</p>
        </div>
        {canView ? (canExport ? exportButton : <Tooltip content={t("console.noPermission")}><span className="inline-flex">{exportButton}</span></Tooltip>) : null}
      </div>
      {!canView ? (
        <p className="px-5 py-4 text-xs text-fg-muted">{t(`${K}.needsView`)}</p>
      ) : activity.isPending ? (
        <div className="space-y-2 px-5 py-4" role="status" aria-label={t("common.loading")}>
          {Array.from({ length: ROWS }, (_, index) => (
            <Skeleton key={index} className="h-5 w-full" />
          ))}
        </div>
      ) : activity.isError ? (
        <ErrorState headingLevel={3} title={t(`${K}.failedTitle`)} description={t(`${K}.failedBody`)} onRetry={() => void activity.refetch()} retryLabel={t("common.retry")} />
      ) : activity.rows.length === 0 ? (
        <EmptyState icon="scroll-text" headingLevel={3} title={t(`${K}.emptyTitle`)} description={t(`${K}.emptyBody`)} />
      ) : (
        <table className="table" aria-label={t(`${K}.title`)}>
          <thead>
            <tr>
              <th scope="col">{t(`${K}.colTime`)}</th>
              <th scope="col">{t(`${K}.colEvent`)}</th>
            </tr>
          </thead>
          <tbody>
            {activity.rows.map((row) => (
              <EventRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      )}
      {canView ? (
        <div className="flex flex-wrap justify-end border-t border-line px-5 py-3">
          <AppLink href={consoleRoutes.settings(summary.slug, "audit")} className={buttonClasses({ variant: "secondary" })} data-testid="security-activity-all">
            <Icon name="scroll-text" />
            {t(`${K}.viewAll`)}
          </AppLink>
        </div>
      ) : null}
    </section>
  );
}
